/**
 * The Outcomes detail rail. Always shows the selected capability: what it is and
 * the information it needs from the conversation.
 */
import { ArrowRight } from "lucide-react";
import type { OutcomeTree } from "../types";
import { capabilityQuestions } from "./CapabilityStructure";
import { SectionLabel } from "./SectionLabel";

interface OutcomeDetailRailProps {
  outcome: OutcomeTree;
  onOpenFullCapability: () => void;
}

export const OutcomeDetailRail = ({
  outcome,
  onOpenFullCapability,
}: OutcomeDetailRailProps) => {
  const questions = capabilityQuestions(outcome.template);

  return (
    <aside className="flex w-[396px] flex-none flex-col overflow-hidden rounded-lg border border-unlikely/30 bg-brand-card">
      <header className="flex flex-none flex-col gap-1.5 border-b border-brand-border px-4 py-3.5">
        <h2 className="text-[15px] font-semibold">{outcome.title}</h2>
        {outcome.description && (
          <p className="text-[13px] leading-normal text-brand-muted">
            {outcome.description}
          </p>
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-4 py-3.5">
        {questions.length > 0 && (
          <section className="flex flex-col gap-2">
            <SectionLabel>Information to obtain</SectionLabel>
            <ol className="flex flex-col gap-1.5 text-[13px] leading-snug text-brand-muted">
              {questions.map((question, index) => (
                <li key={question} className="flex gap-2">
                  <span className="font-semibold text-unlikely">
                    {index + 1}
                  </span>
                  <span>{question}</span>
                </li>
              ))}
            </ol>
          </section>
        )}

        {questions.length === 0 && (
          <p className="text-[13px] leading-snug text-brand-muted">
            This outcome needs nothing from the conversation.
          </p>
        )}

        <span className="flex-1" />

        <div className="flex flex-none flex-col gap-2.5">
          <p className="flex items-center gap-2.5 rounded-lg border border-unlikely/20 bg-unlikely/[0.04] px-3 py-2.5 text-xs text-brand-muted">
            <ArrowRight
              className="h-[15px] w-[15px] flex-none text-unlikely"
              aria-hidden={true}
            />
            When this turns true, the agent may suggest the product. Until then
            it may not.
          </p>
          <button
            type="button"
            onClick={onOpenFullCapability}
            className="inline-flex h-[34px] items-center justify-center rounded-lg bg-unlikely text-[13px] font-semibold text-white transition-colors hover:bg-unlikely-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40 focus-visible:ring-offset-2"
          >
            View capability
          </button>
        </div>
      </div>
    </aside>
  );
};
